
import { DfrData, PDiagramData, Customer, CustomerRequirement, Product, ProductFeature, BotConfig } from '../types';
import { bomSeedData } from './seed/bom-seed';
import { materialsSeedData } from './seed/materials-seed';
import { fmeaSeedData } from './seed/fmea-seed';
import { controlsSeedData } from './seed/controls-seed';
import { testSpecifications, testResults, dvprSummary } from './seed/dvpr-test-seed';
import { ecnLog, ecnSummary } from './seed/ecn-log-seed';
import { iterationHistory, iterationSummary } from './seed/iteration-history-seed';

// ============================================
// MASTER FUNCTIONS & COLORS
// ============================================

export const MASTER_FUNCTIONS: string[] = [
  'Enclosure Integrity', 'Battery System', 'Power Control', 'Electrical Distribution',
  'Motor Performance', 'Shaft Train', 'Frothing Performance', 'Sealing and Protection', 'Labeling and Compliance'
];

export const functionColorMap: Record<string, string> = (() => {
  const palette = ['#7E57C2', '#EF5350', '#26A69A', '#FFA726', '#42A5F5', '#8D6E63', '#66BB6A', '#AB47BC', '#FF7043'];
  const m: Record<string, string> = {};
  MASTER_FUNCTIONS.forEach((fn, i) => m[fn] = palette[i % palette.length]);
  return m;
})();

// ============================================
// DATA DEFINITIONS
// ============================================

const customers: Customer[] = [
  { id: 'CU-001', name: 'Premium Appliance Corp', countryId: 'CO-001', industry: 'Kitchen Appliances', tier: 'OEM' },
  { id: 'CU-002', name: 'EuroHome Brands', countryId: 'CO-003', industry: 'Kitchen Appliances', tier: 'OEM' },
  { id: 'CU-003', name: 'Coffee Equipment Supply', countryId: 'CO-001', industry: 'Foodservice Equipment', tier: 'Tier 1' },
  { id: 'CU-004', name: 'Global Kitchen Solutions', countryId: 'CO-005', industry: 'Kitchen Appliances', tier: 'OEM' }
];

const customerRequirements: CustomerRequirement[] = [
  { id: 'CR-001', customerId: 'CU-001', requirementText: 'Frother quickly produces rich, stable foam for common coffee drinks', type: 'Performance', priority: 'Critical', metric: 'Foam volume increase', target: '≥ 50% increase in 165ml milk within 30s', linkedProductFeatureIds: ['PF-001', 'PF-005'] },
  { id: 'CR-002', customerId: 'CU-001', requirementText: 'Product is easy and intuitive to use', type: 'Usability', priority: 'High', metric: 'User success rate', target: 'Single button operation, <5N activation force', linkedProductFeatureIds: ['PF-002'] },
  { id: 'CR-003', customerId: 'CU-001', requirementText: 'Comfortable to hold and operate', type: 'Usability', priority: 'Medium', metric: 'Ergonomic rating', target: 'Handle diameter 32-35mm', linkedProductFeatureIds: ['PF-003'] },
  { id: 'CR-004', customerId: 'CU-002', requirementText: 'Operates reliably every time', type: 'Performance', priority: 'Critical', metric: 'Start success rate', target: 'MTTF ≥ 5,000 cycles (95% confidence)', linkedProductFeatureIds: ['PF-002', 'PF-004'] },
  { id: 'CR-005', customerId: 'CU-002', requirementText: 'Adequate runtime per battery set', type: 'Performance', priority: 'High', metric: 'Cycles per battery', target: '≥ 150 cycles (30s each) per 2 AA Alkaline cells', linkedProductFeatureIds: ['PF-004'] },
  { id: 'CR-006', customerId: 'CU-001', requirementText: 'Safe in normal use', type: 'Safety', priority: 'Critical', metric: 'Safety compliance', target: 'Compliant with UL 1026 and FDA 21 CFR 117', linkedProductFeatureIds: ['PF-006', 'PF-007'] },
  { id: 'CR-007', customerId: 'CU-003', requirementText: 'Resists splashes and cleaning', type: 'Durability', priority: 'High', metric: 'IP Rating', target: 'IPX4 ingress protection', linkedProductFeatureIds: ['PF-006'] },
  { id: 'CR-008', customerId: 'CU-004', requirementText: 'Acceptable noise and vibration', type: 'Usability', priority: 'Medium', metric: 'Sound level', target: '≤ 65 dB(A) at 1 meter', linkedProductFeatureIds: ['PF-001', 'PF-005'] },
  { id: 'CR-009', customerId: 'CU-001', requirementText: 'Food-safe and easy to clean', type: 'Regulatory', priority: 'Critical', metric: 'Food safety', target: '304 Stainless Steel or better for food-contact', linkedProductFeatureIds: ['PF-007'] },
  { id: 'CR-010', customerId: 'CU-002', requirementText: 'Clear labeling and compliance', type: 'Regulatory', priority: 'Medium', metric: 'Markings', target: 'Laser-etched compliance logos (CE, FCC, UKCA)', linkedProductFeatureIds: ['PF-008'] }
];

const products: Product[] = [
  { id: 'PR-001', productLineId: 'PL-001', name: 'Handheld Liquid Aerator (Milk Frother)', description: 'Battery-operated milk frother', partNumber: 'AERATOR-MF-2025-V1', status: 'Development', features: ['Single button', 'Stainless whisk', 'Ergonomic grip', 'Battery operated', 'Food-safe'], linkedCustomerReqIds: ['CR-001', 'CR-002', 'CR-003', 'CR-004', 'CR-005', 'CR-006', 'CR-007', 'CR-008', 'CR-009', 'CR-010'] }
];

const productFeatures: ProductFeature[] = [
  { id: 'PF-001', productId: 'PR-001', name: 'High-speed frothing', description: 'Motor-driven whisk', linkedCustomerReqIds: ['CR-001', 'CR-008'], linkedBomItemIds: [6, 14] },
  { id: 'PF-002', productId: 'PR-001', name: 'Single button control', description: 'Simple ON/OFF', linkedCustomerReqIds: ['CR-002', 'CR-004'], linkedBomItemIds: [8, 9] },
  { id: 'PF-003', productId: 'PR-001', name: 'Ergonomic design', description: 'Comfortable grip', linkedCustomerReqIds: ['CR-003'], linkedBomItemIds: [1, 2] },
  { id: 'PF-004', productId: 'PR-001', name: 'Battery power', description: 'Standard AA batteries', linkedCustomerReqIds: ['CR-004', 'CR-005'], linkedBomItemIds: [3, 4, 5, 12] },
  { id: 'PF-005', productId: 'PR-001', name: 'Quiet operation', description: 'Low noise motor', linkedCustomerReqIds: ['CR-008'], linkedBomItemIds: [6, 7, 14] },
  { id: 'PF-006', productId: 'PR-001', name: 'Splash resistant', description: 'Sealed design', linkedCustomerReqIds: ['CR-006', 'CR-007'], linkedBomItemIds: [16, 17, 19] },
  { id: 'PF-007', productId: 'PR-001', name: 'Food-safe materials', description: 'FDA-compliant', linkedCustomerReqIds: ['CR-006', 'CR-009'], linkedBomItemIds: [13, 14] },
  { id: 'PF-008', productId: 'PR-001', name: 'Regulatory compliance', description: 'Proper labeling', linkedCustomerReqIds: ['CR-010'], linkedBomItemIds: [20] }
];

// Local definitions for P-Diagrams and Customers (kept as they are used)





const pDiagramList: PDiagramData[] = MASTER_FUNCTIONS.map((fn, index) => ({
  consolidatedFunctionId: fn,
  systemName: fn,
  goal: ['To achieve the function reliably under all conditions.'],
  inputSignals: ['User input', 'Electrical energy'],
  controlFactors: ['Material properties', 'Geometric dimensions'],
  systemFunction: [`Performs the task of: ${fn}`],
  expectedOutput: ['Desired functional output'],
  unexpectedOutput: ['Noise', 'Heat', 'Vibration', 'EMF'],
  noiseFactors: {
    pieceToPiece: ['Manufacturing variation'],
    changeOverTime: ['Material degradation', 'Wear'],
    customerUsage: ['Misuse', 'Extreme duty cycles'],
    externalEnvironment: ['Temperature extremes', 'Humidity'],
    internal: ['Component interaction']
  },
  // First 3 functions are draft, rest are active (for testing draft view)
  status: (index < 3 ? 'draft' : 'active') as 'draft' | 'active'
}));



// --- BOT CONFIGURATION MOCK DATA ---
const botConfigs: BotConfig[] = [
  {
    id: 'BOT-FMEA-01',
    name: 'FMEA Drafter',
    description: 'Generates FMEA rows from P-Diagrams.',
    role: 'You are a reliability engineer. Given a system P-Diagram, list potential failure modes.',
    activeRevisionId: 'REV-002',
    revisions: [
      { id: 'REV-001', version: 1, author: 'admin', timestamp: '2023-01-15T10:00:00Z', content: 'List failure modes for this system.' },
      { id: 'REV-002', version: 2, author: 'admin', timestamp: '2023-02-20T14:30:00Z', content: 'You are an expert facilitator. Analyze the provided P-Diagram variables and output a JSON list of failure modes with high severity rankings.' }
    ]
  },
  {
    id: 'BOT-MAGNUS-01',
    name: 'Magnus Avatar (Navigator)',
    description: 'The supportive floating assistant for product navigation and quick insights.',
    role: 'You are Magnus, the friendly avatar of the Living Reliability system. Your mission is to help users navigate the complex DfR application and provide quick, conversational insights into their data. Keep your tone supportive, technical but accessible, and always offer to help them find what they need.',
    activeRevisionId: 'REV-MAG-01',
    revisions: [
      { id: 'REV-MAG-01', version: 1, author: 'system', timestamp: '2023-01-01T00:00:00Z', content: 'You are Magnus, the friendly avatar of the Living Reliability system. Your mission is to help users navigate the complex DfR application and provide quick, conversational insights into their data.' }
    ]
  },
  {
    id: 'BOT-ANALYST-01',
    name: 'Executive Reliability Analyst',
    description: 'The expert backend agent for deep FMEA analysis and reporting.',
    role: 'You are Magnus Prime, a Senior Reliability Engineer and FMEA Analyst. Your mission is to provide comprehensive, data-driven Executive-Quality Reports. You specialize in identifying root causes, calculating RPN risks, and recommending design/material optimizations. Your tone is formal, objective, and deeply technical.',
    activeRevisionId: 'REV-ANA-01',
    revisions: [
      { id: 'REV-ANA-01', version: 1, author: 'system', timestamp: '2023-12-19T00:00:00Z', content: 'You are Magnus Prime, an expert Reliability Engineer. Provide structured executive summaries and data-driven recommendations.' }
    ]
  }
];

// ============================================
// MAIN EXPORT
// ============================================

export const dfrData: DfrData = {
  // Project Metadata (Metadata Standards v2.0.0)
  projectMetadata: {
    id: 'proj-aerator-pro-001',
    name: 'Aerator Pro',
    displayName: 'Aerator Pro - Milk Frother',
    description: 'High-performance battery-operated milk frother with precision BLDC motor and high-durability seals for consumer and professional kitchen environments.',
    created: '2024-01-15T00:00:00Z',
    lastModified: new Date().toISOString(),
    version: '1.17.0',
    schemaVersion: '2.0.0',
    author: 'Magnus Team',
    createdBy: 'admin',
    modifiedBy: 'system',
    projectType: 'Production',
    developmentPhase: 'Production',
    isExperimental: false,
    status: 'active',
    tags: ['kitchen-appliances', 'consumer-product', 'battery-powered'],
    marketSegment: 'Consumer Kitchen Appliances',
    targetCertifications: ['CE', 'UL', 'FDA'],
    changeLog: [{
      timestamp: '2024-01-15T00:00:00Z',
      user: 'admin',
      changeType: 'created',
      description: 'Project initialized with seed data from 17 iterations',
    }]
  },

  // Core FMEA data (use seed data with comprehensive FMEA items)
  fmeaItems: fmeaSeedData as any[],

  // BOM with full component details, materials, suppliers
  bomItems: bomSeedData as any[],

  // P-Diagrams (use local definitions enriched with FULL metadata)
  pDiagramList: pDiagramList.map(p => ({
    ...p,
    projectName: 'Aerator Pro',
    epochId: 17  // Add epochId for metadata compliance
  })),

  // DVP&R Tests (use comprehensive seed data with 12 test specs), mapped to project
  // Convert TestSpecification format to DvprTest format with proper hypertags
  dvprTests: testSpecifications.map((t: any) => ({
    testId: t.testId,
    testName: t.testName,
    functionName: t.linkedFmeaIds?.[0] || 'Unknown Function', // Use first linked FMEA as function reference
    traceableRiskId: t.linkedFmeaIds || [],
    procedure: t.testMethod || t.procedure || '',
    criteria: t.objectiveStatement || t.criteria || '',
    projectName: 'Aerator Pro',
    epochId: 17 // Latest iteration epoch
  })),

  // Controls (prevention/detection) - use seed data
  controls: controlsSeedData as any[],

  // Customers and requirements (Enriched with Project)
  customers: customers.map(c => ({ ...c, projectName: 'Aerator Pro' })),
  customerRequirements: customerRequirements.map(c => ({ ...c, projectName: 'Aerator Pro' })),
  products: products.map(p => ({ ...p, projectName: 'Aerator Pro' })),
  productFeatures: productFeatures.map(f => ({ ...f, projectName: 'Aerator Pro' })),
  botConfigs: botConfigs.map(b => ({ ...b, projectName: 'Aerator Pro' })),

  // Materials database (8 validated materials)
  materials: materialsSeedData.map((m: any) => ({ ...m, projectName: 'Aerator Pro' })),

  // ========== PBS REVISION CONTROL (Metadata Standards v2.0.0) ==========

  // PBS Revision Levels - Design maturity tracking
  pbsRevisions: [
    {
      id: 'PBS-REV-ALPHA-1',
      name: 'Alpha 1',
      stage: 'alpha-1',
      createdDate: '2024-01-15T00:00:00Z',
      createdBy: 'admin',
      description: 'Initial design baseline - proof of concept',
      isActive: false,
      status: 'complete',
      projectName: 'Aerator Pro',
      itemCount: 15
    },
    {
      id: 'PBS-REV-BETA-1',
      name: 'Beta 1',
      stage: 'beta-1',
      createdDate: '2024-06-01T00:00:00Z',
      createdBy: 'WK',
      description: 'Design refinements after validation testing',
      carriedOverFrom: 'PBS-REV-ALPHA-1',
      isActive: false,
      status: 'complete',
      projectName: 'Aerator Pro',
      itemCount: 18
    },
    {
      id: 'PBS-REV-PROD-1',
      name: 'Production 1.0',
      stage: 'p-level',
      createdDate: '2024-11-15T00:00:00Z',
      createdBy: 'MC',
      description: 'Production release - fully validated',
      carriedOverFrom: 'PBS-REV-BETA-1',
      isActive: true,
      status: 'released', // 'released' is not in PbsRevisionStatus? Wait.
      // types/pbsRevision.ts: 'planning' | 'building' | 'testing' | 'complete' | 'archived'.
      // I should use 'complete' or 'testing' depending on reality. Let's use 'complete' as it's prod, or 'building'.
      // Actually, 'released' is NOT in the type. 'complete' is best fit for "Done/Released".
      projectName: 'Aerator Pro',
      itemCount: 20
    }
  ] as any[], // Casting as any to avoid strict type checks here, should be PbsRevisionLevel[]

  // PBS Items - Component revision tracking (Derived from BOM Seed for consistency)
  pbsItems: bomSeedData.map((b: any) => ({
    id: `PBS-ITEM-${b.id}`,
    pbsNumber: `100-${b.id.toString().padStart(3, '0')}`,
    name: b.component,
    type: b.childIds && b.childIds.length > 0 ? 'ASSEMBLY' : 'DESIGNED PART',
    revisionLevel: 'PBS-REV-PROD-1', // Assign to Production Rev for visibility
    partNumber: b.partNumber || `PN-${b.id}`,
    partRevision: 'A',
    quantity: b.quantity || 1,
    reviewStatus: 'confirmed',
    frozen: true,
    status: 'released',
    owner: 'Admin',
    projectName: 'Aerator Pro',
    epochId: 17,
    comment: b.description || '',
    bomItemId: b.id // Link to BOM for Rich Data
  })) as any[],

  // ========== COMPREHENSIVE SEED DATA FROM 17 ITERATIONS ==========

  // ECN Log - Engineering Change Notices tracking design evolution
  ecnLog: ecnLog.map((e: any) => ({ ...e, projectName: 'Aerator Pro' })), // Enrich with projectName
  ecnSummary: ecnSummary, // ECN statistics, cost, effectiveness

  // Iteration History - Complete tracking from I1 to I17
  iterationHistory: iterationHistory, // 17 iterations with scoring changes, milestones, pass rates
  iterationSummary: iterationSummary, // Summary statistics across all iterations

  // LIMS Test Data - Comprehensive test results
  testSpecifications: testSpecifications as any[], // 12 test specifications with FMEA linkage
  testResults: testResults.map(r => ({ ...r, projectName: 'Aerator Pro' })) as any[], // 12 tests × 17 iterations = 204 test results
  testSummary: dvprSummary, // Test summary statistics
};
